import { Component, Watch, Prop } from "vue-property-decorator";
import { Block } from '../../../helpers/components';
import { BaseBlock } from '../../base-block';
import { ApartmentSearchFilterDTO } from "@/model/ApartmentSearchFilterDTO";
import { LocationStatsDTO } from "@/model/LocationStatsDTO";
import { formatDateLocalISO } from "../../../helpers/dates";
import { LocationDTO } from '../../../model/LocationDTO';
import { ApartmentSearchSortType } from '../../../enums/ApartmentSearchSortType';
import { ApartmentSearchSortDirection } from '../../../enums/ApartmentSearchSortDirection';
import { apartmentsOverviewSearchData } from './apartments-overview-search.data';
import { Language } from '@/enums/Language';
import { parse } from 'qs';
import { formatNumber} from '@/helpers/numbers';


import { intersection, uniq, uniqBy, orderBy, range, flatten } from 'lodash-es';

interface FormValues {
	priceRange: [number, number];
	minPrice?: number;
	maxPrice?: number;
	layouts: number[];
	housingBlocks: number[];
	floorLevels: number[];
	completionDate?: Date;
}

enum ComponentType {
	Floor = 2,
	Location = 3,
	HousingBlock = 4
}

interface LayoutViewerDTO {
	componentId: number;
	componentType: ComponentType;
	housingBlockId?: number;
}

interface FloorStats {
	floorLevel: number;
	floorName: string;
	sold: boolean;
}

interface LayoutStats {
	layoutId: number;
	layoutLabel: string;
	layoutRoomCount: number;
	sold: boolean;
}

interface HousingBlockStats {
	housingBlockId: number;
	housingBlockName: string;
	sold: boolean;
}

enum Resolution {
	SM = 576,
	MD = 776,
	LG = 992,
	XL = 1200
}

enum DefaultInplanHeight {
	XS = 300,
	SM = 350,
	MD = 400,
	LG = 450,
	XL = 600
}

@Block(apartmentsOverviewSearchData)
@Component
export class ApartmentsOverviewSearchBlock extends BaseBlock {
	@Prop() location: LocationDTO;
	@Prop() locationStats: LocationStatsDTO;

	currentLayoutViwerId: number;
	frameHeight: number = 200;

	showHousingBlockDropdown: boolean = false;
	showPriceDropdown: boolean = false;
	showLayoutDropdown: boolean = false;
	showFloorDropdown: boolean = false;
	priceRange: [string,string] = ['', ''];
	priceRangeMin: string = '';

	formValues: FormValues = {
		priceRange: [0, 0],
		minPrice: 0,
		maxPrice: 0,
		layouts: [],
		floorLevels: [],
		housingBlocks: [],
		completionDate: null
	};

	get floorStats(): FloorStats[] {
		return orderBy(uniqBy(this.locationStats.overviewStats.map(s => ({
			floorLevel: s.floorLevel,
			floorName: s.floorName
		})), s => s.floorLevel).map(s => ({
			floorLevel: s.floorLevel,
			floorName: s.floorName,
			sold: this.locationStats.overviewStats.filter(f => f.floorLevel == s.floorLevel).every(s => s.sold)
		})), ["floorLevel"], ["asc"]);
	}

	get housingBlockStats(): HousingBlockStats[] {
		return orderBy(uniqBy(this.locationStats.overviewStats.map(s => ({
			housingBlockId: s.housingBlockId,
			housingBlockName: s.housingBlockName,
		})), s => s.housingBlockId).map(m => ({
			...m,
			sold: this.locationStats.overviewStats.filter(f => f.housingBlockId == m.housingBlockId).every(f => f.sold)
		})), ["housingBlockName"], ["asc"]);
	}

	get layoutStats(): LayoutStats[] {
		return orderBy(uniqBy(this.locationStats.overviewStats.map(s => ({
			layoutId: s.layoutId,
			layoutLabel: s.layoutLabel,
			layoutRoomCount: s.layoutRoomCount,
		})), s => s.layoutId).map(s => ({
			...s,
			sold: this.locationStats.overviewStats.filter(f => f.layoutId == s.layoutId).every(f => f.sold)
		})), ["layoutRoomCount"], ["asc"]);
	}

	get isRuLang() {
		return this.$store.langId === Language.Ru;
	}

	created() {
	
		var tmpFormValues: FormValues = { ...this.formValues };
		//this.priceRange = [this.minPriceOption, this.maxPriceOption];
		tmpFormValues.minPrice = this.minPriceOption;
		tmpFormValues.maxPrice = this.maxPriceOption;

		tmpFormValues.priceRange = [this.minPriceOption, this.maxPriceOption];
		tmpFormValues.floorLevels = [
			...uniq(this.locationStats.overviewStats./*filter(f => !f.sold).*/map(i => i.floorLevel))
		];
		tmpFormValues.housingBlocks = [
			...uniq(this.locationStats.overviewStats./*filter(f => !f.sold).*/map(i => i.housingBlockId))
		];
		tmpFormValues.layouts = [
			...uniq(this.locationStats.overviewStats./*filter(f => !f.sold).*/map(m => m.layoutRoomCount))
		];
		if (this.locationStats.availableNotCompletedApartmentCount > 0) {
			if (this.completionDateOptions.length > 0) {
				tmpFormValues.completionDate = this.allCompletionDateOption;
			}
		} else {
			tmpFormValues.completionDate = null;
		}

		var queryString = this.$route.query as ApartmentSearchFilterDTO;
		if (queryString) {
			if (queryString.minPrice) {
				tmpFormValues.priceRange[0] = parseInt(queryString.minPrice.toString());
				tmpFormValues.minPrice = parseInt(queryString.minPrice.toString());
			}

			if (queryString.maxPrice) {
				tmpFormValues.priceRange[1] = parseInt(queryString.maxPrice.toString());
				tmpFormValues.maxPrice = parseInt(queryString.maxPrice.toString());
			}

			if (queryString.layoutRoomCounts) {
				tmpFormValues.layouts = queryString.layoutRoomCounts.map(m => parseInt(m.toString()));
			}

			if (queryString.housingBlockIds) {
				tmpFormValues.housingBlocks = queryString.housingBlockIds.map(m => parseInt(m.toString()));
			}

			if (queryString.floorLevels) {
				tmpFormValues.floorLevels = queryString.floorLevels.map(m => parseInt(m.toString()));
			}

			if (this.locationStats.allApartmentsCompleted) {
				tmpFormValues.completionDate = null;
			} else {
				if (queryString.completedOnly) {
					tmpFormValues.completionDate = null;
				}
				else if (queryString.completionDate) {
					const completionDate = new Date(queryString.completionDate);

					if (completionDate.getMonth() <= 5) {
						tmpFormValues.completionDate = new Date(completionDate.getFullYear(), 5, 30);
					} else {
						tmpFormValues.completionDate = new Date(completionDate.getFullYear(), 11, 31);
					}
				}
			}
		}



		this.formValues = { ...tmpFormValues };
		this.search();
	}

	mounted() {
		window.addEventListener("message", event => {
			if (event.origin === this.$store.settings.layoutViewerUrl) {
				var data = JSON.parse(event.data);

				if (data.loading) {
					this.currentLayoutViwerId = this.location.id;

					if (this.formValues.housingBlocks.length != this.housingBlockStats.length || this.formValues.floorLevels.length != this.floorStats.length) {
						this.setLayoutViewerFromForm(this.formValues.housingBlocks, this.formValues.floorLevels);
					}
				} else {
					var dto: LayoutViewerDTO = {
						componentId: data.id,
						componentType: data.type,
						housingBlockId: data.bd
					};

					this.setFormFromLayourViewer(dto);
				}
			}
		});

		window.addEventListener('resize', this.matchHeight);

		this.matchHeight()
	}

	beforeDestroy() {
		window.removeEventListener('resize', this.matchHeight)
	}

	/* LayoutViewer */

	blockUpdateLayoutViewer: boolean = false;

	get layoutViewerSrc() {
		let src = `${this.$store.settings.layoutViewerUrl}/LayoutWms3.aspx`;

		let queryParams = [
			`languageVersionId=${this.$store.langId}`,
			`timeVersionId=${this.$store.timeId}`,
			`pageId=${this.$store.page.id}`,
			`blockId=${this.block.id}`,
			`objectId=${this.location.id}`,
			`componentType=3`,
			`backPossible=True`,
			`domain=${window.location.host}`
		];

		return `${src}?${queryParams.join('&')}`;
	}

	setLayoutViewerFromForm(housingBlockIds: number[], floorLevels: number[]) {
		if (this.blockUpdateLayoutViewer) {
			this.blockUpdateLayoutViewer = false;
			return;
		}

		if (housingBlockIds.length === 1) {
			if (floorLevels.length === 1) {
				this.updateLayoutViewer(
					this.locationStats.overviewStats.find(f => f.housingBlockId == housingBlockIds[0] && f.floorLevel == floorLevels[0]).floorId,
					ComponentType.Floor,
					housingBlockIds[0]
				);
			} else {
				this.updateLayoutViewer(
					housingBlockIds[0],
					ComponentType.HousingBlock,
					housingBlockIds[0]
				);
			}
		} else {
			this.updateLayoutViewer(
				this.location.id,
				ComponentType.Location
			);
		}
	}

	updateLayoutViewer(componentId: number, componentType: ComponentType, housingBlockId?: number) {
		if (!this.$refs.frame) {
			return;
		}

		if (this.currentLayoutViwerId === componentId) {
			return;
		}

		(this.$refs.frame as HTMLIFrameElement).contentWindow.postMessage(
			JSON.stringify({
				id: componentId,
				type: componentType,
				bd: housingBlockId
			}),
			"*"
		);

		this.currentLayoutViwerId = componentId;
	}

	setFormFromLayourViewer(dto: LayoutViewerDTO) {
		this.blockUpdateLayoutViewer = true;

		this.currentLayoutViwerId = dto.componentId;
		let tmpFormValues: FormValues = {...this.formValues};

		if (dto.componentType == ComponentType.Location) {
			tmpFormValues.priceRange = [this.minPriceOption, this.maxPriceOption];
			tmpFormValues.minPrice = this.minPriceOption;
			tmpFormValues.maxPrice = this.maxPriceOption;
			tmpFormValues.floorLevels = [
				...uniq(this.locationStats.overviewStats./*filter(f => !f.sold).*/map(i => i.floorLevel))
			];
			tmpFormValues.housingBlocks = [
				...uniq(this.locationStats.overviewStats./*ffilter(f => !f.sold).*/map(i => i.housingBlockId))
			];
			tmpFormValues.layouts = [
				...uniq(this.locationStats.overviewStats./*filter(f => !f.sold).*/map(m => m.layoutRoomCount))
			];
		}

		if (dto.componentType == ComponentType.HousingBlock) {
			tmpFormValues.housingBlocks = [dto.componentId];
			tmpFormValues.floorLevels = [
				...intersection(
					this.floorStats./*filter(f => !f.sold).*/map(m => m.floorLevel),
					uniq(this.locationStats.overviewStats.filter(f => f.housingBlockId == dto.componentId).map(m => m.floorLevel))
				)
			];
			tmpFormValues.layouts = [
				...intersection(
					this.layoutStats./*filter(f => !f.sold).*/map(m => m.layoutRoomCount),
					uniq(this.locationStats.overviewStats.filter(f => f.housingBlockId == dto.componentId).map(m => m.layoutRoomCount))
				)
			];
			tmpFormValues.priceRange = [
				Math.floor(Math.min(...this.locationStats.overviewStats.filter(f => f.housingBlockId == dto.componentId /* && !f.sold*/).map(m => m.minPrice)) / 1e6) * 1e6,
				Math.ceil(Math.max(...this.locationStats.overviewStats.filter(f => f.housingBlockId == dto.componentId /* && !f.sold*/).map(m => m.maxPrice)) / 1e6) * 1e6
			];
		}

		if (dto.componentType == ComponentType.Floor) {
			tmpFormValues.housingBlocks = [dto.housingBlockId];
			tmpFormValues.floorLevels = [
				...intersection(
					this.floorStats./*filter(f => !f.sold).*/map(m => m.floorLevel),
					uniq(this.locationStats.overviewStats.filter(f => f.housingBlockId == dto.housingBlockId && f.floorId == dto.componentId).map(m => m.floorLevel))
				)
			];
			tmpFormValues.layouts = [
				...intersection(
					this.layoutStats./*filter(f => !f.sold).*/map(m => m.layoutRoomCount),
					uniq(this.locationStats.overviewStats.filter(f => f.housingBlockId == dto.housingBlockId && f.floorId == dto.componentId).map(m => m.layoutRoomCount))
				)
			];
			tmpFormValues.priceRange = [
				Math.floor(Math.min(...this.locationStats.overviewStats.filter(f => f.housingBlockId == dto.housingBlockId && f.floorId == dto.componentId).map(m => m.minPrice)) / 1e6) * 1e6,
				Math.ceil(Math.max(...this.locationStats.overviewStats.filter(f => f.housingBlockId == dto.housingBlockId && f.floorId == dto.componentId).map(m => m.maxPrice)) / 1e6) * 1e6
			];
			tmpFormValues.minPrice = Math.floor(Math.min(...this.locationStats.overviewStats.filter(f => f.housingBlockId == dto.housingBlockId && f.floorId == dto.componentId).map(m => m.minPrice)) / 1e6) * 1e6;
			tmpFormValues.maxPrice = Math.ceil(Math.max(...this.locationStats.overviewStats.filter(f => f.housingBlockId == dto.housingBlockId && f.floorId == dto.componentId).map(m => m.maxPrice)) / 1e6) * 1e6;
		}

		this.formValues = {...tmpFormValues};

		this.search();
	}

	@Watch("formValues")
	floorLevelsChanged(formValues: FormValues) {
		this.setLayoutViewerFromForm(formValues.housingBlocks, formValues.floorLevels);
	}

	formOnChange() {
		this.setLayoutViewerFromForm(this.formValues.housingBlocks, this.formValues.floorLevels);
	}

	/* Price */
	get minPriceOption() {
		return Math.floor(this.locationStats.minAvailablePrice / 1e6) * 1e6;
	}

	get maxPriceOption() {
		return Math.ceil(this.locationStats.maxAvailablePrice / 1e6) * 1e6;
	}

	get minPriceSelected() {
		return this.formValues.minPrice > this.minPriceOption;
	}

	get maxPriceSelected() {
		return this.formValues.maxPrice < this.maxPriceOption;
	}

	get priceSelected() {
		return this.minPriceSelected || this.maxPriceSelected;
	}

	get priceMin() {
		return formatNumber(this.formValues.minPrice);
	}

	set priceMin(newValue: string) {
		this.formValues.minPrice = this.formatStringToNumber(newValue);
	}

	get priceMax() {
		return formatNumber(this.formValues.maxPrice);
	}

	set priceMax(newValue: string) {
		this.formValues.maxPrice = this.formatStringToNumber(newValue);
	}

	formatStringToNumber(newValue: string): number {
		const unFormatValue = newValue.replace(/\D/g, '');
		return isNaN(parseInt(unFormatValue)) ? 0 : parseInt(unFormatValue);
	}


	/* Floor level */
	get minFloorLevelOption() {
		return this.locationStats.minAvailableFloorLevel;
	}

	get maxFloorLevelOption() {
		return this.locationStats.maxAvailableFloorLevel;
	}

	get floorLevelSelected() {
		return this.formValues.floorLevels.length > 0;
	}

	/* Layout */
	get layoutsSelected() {
		return this.formValues.layouts.length > 0;
	}

	/* Housing Blocks */
	get housingBlocksSelected() {
		return this.formValues.housingBlocks.length > 0;
	}

	get showCompletionDateFilter() {
		if (this.locationStats.allApartmentsCompleted) {
			return false;
		}

		const dates = uniq(this.locationStats.overviewStats.filter(f => f.apartmentCompletionDate && new Date(f.apartmentCompletionDate) >= this.getCurrentDate()).map(m => new Date(m.apartmentCompletionDate).toISOString()));
		if (this.locationStats.availableCompletedApartmentCount === 0 && dates.length < 2) {
			return false;
		}

		return true;
	}

	/* Completion date */
	get completionDateOptions() {
		if (!this.locationStats || this.locationStats.allApartmentsCompleted) {
			return [];
		}

		const lastCompletionDate = new Date(this.locationStats.lastApartmentCompletionDate);

		const currentDate = new Date() > new Date(this.locationStats.nextApartmentCompletionDate) ? new Date() : new Date(this.locationStats.nextApartmentCompletionDate);
		const yearRange = range(currentDate.getFullYear(), lastCompletionDate.getFullYear() + 1);
		const options = flatten(
			yearRange.map(year => [
				new Date(year, 5, 30), // half of a year (30th June)
				new Date(year, 11, 31) // end of a year (31th December)
			])
		);

		if (currentDate.getMonth() > 5) {
			// remove half of current year option if today is at least July
			options.shift();
		}

		if (lastCompletionDate.getMonth() <= 5) {
			// remove end of max complention year option if max completion date is at most June
			options.pop();
		}

		return options;
	}

	get allCompletionDateOption() {
		return new Date(2999, 11, 31);
	}

	get hasCompletedApartment() {
		return this.locationStats.availableCompletedApartmentCount > 0;
	}

	/* Search */
	search() {
		let searchFilter: ApartmentSearchFilterDTO = {};

		/* Layouts */
		if (this.layoutsSelected) {
			searchFilter.layoutRoomCounts = [...this.formValues.layouts];
		}

		/* Price */
		if (this.minPriceSelected) {
			searchFilter.minPrice = this.formValues.minPrice;
		}

		if (this.maxPriceSelected) {
			searchFilter.maxPrice = this.formValues.maxPrice;
		}

		/* Floor Level */
		if (this.floorLevelSelected) {
			searchFilter.floorLevels = [...this.formValues.floorLevels];
		}

		/* Housing Blocks */
		if (this.housingBlocksSelected) {
			searchFilter.housingBlockIds = [...this.formValues.housingBlocks];
		}

		/* Completion date */
		if (this.formValues.completionDate) {
			searchFilter.completionDate = formatDateLocalISO(this.formValues.completionDate);
		} else {
			searchFilter.completedOnly = true;
		}

		/* Time version */
		if (this.$route.query.csh) {
			searchFilter.csh = parseInt(this.$route.query.csh);
		}

		/* Preview (for showing preparation time version) */
		if (this.$route.query.predbezne) {
			searchFilter.predbezne = parseInt(this.$route.query.predbezne);
		}

		this.$router.push({path: this.$route.path, query: {
			...searchFilter as any,
			sort: this.$route.query.sort || ApartmentSearchSortType.TotalPrice,
			sortDirection: this.$route.query.sortDirection || ApartmentSearchSortDirection.Up,
			search: true
		}});
	}

	matchHeight() {
		const offsetWidth = document.body.offsetWidth;
		const height = this.getText('inplanHeight').split(';');

		if (offsetWidth >= Resolution.XL) {
			this.frameHeight = parseInt(height[4]) || DefaultInplanHeight.XL;
		} else if (offsetWidth >= Resolution.LG) {
			this.frameHeight = parseInt(height[3]) || DefaultInplanHeight.LG;
		} else if (offsetWidth >= Resolution.MD) {
			this.frameHeight = parseInt(height[2]) || DefaultInplanHeight.MD;
		} else if (offsetWidth >= Resolution.SM) {
			this.frameHeight = parseInt(height[1]) || DefaultInplanHeight.SM;
		} else {
			this.frameHeight = parseInt(height[0]) || DefaultInplanHeight.XS;
		}
	}
}

export default ApartmentsOverviewSearchBlock;
